import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BreakableRenderer, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnComposition, ColumnPanel, MultipleHeaderColumnCompositions, MultipleLineTextRenderer, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellData, ComponentData, NumberFormatter, TabData } from '@ddt-web/data-service';
import { MDSScenarioCategory, MdsScenarioUtil, ScenarioHeaderConfig } from '@ddt-web/scenarios';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingComponent, HeadingLevel } from '../heading/heading-component';
import { TableCompositionHelper } from '../helpers/column-compositions/table-composition-helper';
import { ScenarioData, ScenarioFilterer } from '../helpers/scenario-tester/scenario-filterer';
import { ScenarioHelper } from '../helpers/scenario-tester/scenario-helper';
import { ScenarioTesterResultsDcr } from './st-table-disclaimer.const';

/**
 * This is used in Scenario Tester app
 */
export class ScenarioTesterTableComponent extends BusinessComponent {
	@InjectConfig(SharedConfigKey.ENABLE_SCENARIO_ADVISORY_FEE) private readonly enableScenarioAdvisoryFee: boolean = false;

	private readonly MAX_COL_COUNT = 7;

	private includeYield: boolean;
	private isDollarAmountDefault: boolean;
	private defaultAumValue: number;
	private selectedAssetClassesIdx = [];
	private mdsGroups: MDSScenarioCategory[] = [];
	private validMdsScenarios: MDSScenarioCategory[] = [];

	constructor(
		name: string,
		public dataComponentId: string,
		includeYield: boolean,
		isDollarAmountDefault: boolean,
		defaultAumValue: number,
		mdsGroups: MDSScenarioCategory[] = [],
		public advisoryFee: number = 0,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		this.includeYield = includeYield;
		this.isDollarAmountDefault = isDollarAmountDefault;
		this.defaultAumValue = defaultAumValue;
		this.mdsGroups = mdsGroups;
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		return Boolean(componentData && componentData.success && componentData.tabs);
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];

		if (this.isComponentDataVisible(componentData)) {
			const disclaimerBeforeTableDcr = this.includeYield ? 'ST_DISCLAIMER_BEFORE_RESULTS_TABLE_WITH_YIELD' : 'ST_DISCLAIMER_BEFORE_RESULTS_TABLE_WITHOUT_YIELD';
			const disclaimerAfterTableDcr = this.includeYield ? 'ST_DISCLAIMER_AFTER_RESULTS_TABLE_WITH_YIELD' : 'ST_DISCLAIMER_AFTER_RESULTS_TABLE_WITHOUT_YIELD';

			panels.push(...[new HeadingComponent('SCENARIO_TESTER_TABLE_H1', 'Scenario Tester Results', HeadingLevel.H1)]);

			panels.push(
				new StaticParagraphComponent(disclaimerBeforeTableDcr, ScenarioTesterResultsDcr.ST_DISCLAIMER_BEFORE_RESULTS_TABLE, {}, { marginTop: 5, marginBottom: 10 })
			);

			panels.push(this.addAsOfDateToPanels(componentData));

			const yieldColCount = this.includeYield ? 1 : 0;

			const scenarioData: ScenarioData = ScenarioFilterer.filterSelectedScenarios(componentData.getTabById('scenarios.marketMoves'));
			const yieldTabData: TabData = componentData.getTabById('scenarios.yield');

			// filtering out asset classes
			this.selectedAssetClassesIdx = ScenarioFilterer.filterSelectedAssetClassesIndex(componentData, true);
			scenarioData.cells = scenarioData.cells.filter((cell, idx) => this.selectedAssetClassesIdx.includes(idx));
			const filteredHeadersY = componentData
				.getTabById('scenarios.marketMoves')
				.tableData[0].headersY.filter((header: CellData, idx: number) => header.config && header.config.visible && this.selectedAssetClassesIdx.includes(idx));

			// get valid mds scenarios (with group name and scenarios that are selected) in the order they were selected
			const orderedMdsScenarios = MdsScenarioUtil.getOrderedMdsGroups(this.mdsGroups, scenarioData.headersX);
			this.validMdsScenarios = MdsScenarioUtil.getValidMdsScenarios(orderedMdsScenarios, scenarioData.headersX);
			const noOfScenariosPerMdsGroup = this.validMdsScenarios.map(mdsGroup => mdsGroup.scenarios.length);

			// separating into different tables
			let tableColCountArray: number[] = [];
			tableColCountArray = this.populateTableColCountArray(scenarioData.headersX.length, yieldColCount, tableColCountArray, noOfScenariosPerMdsGroup);
			const shardedScenarioTables = this.sharedScenarioTables(tableColCountArray, scenarioData, yieldTabData);

			// creating the table panels for each table
			shardedScenarioTables.forEach((table, i) => {
				const rows: Array<RowPanel> = [];

				const columnConfigurations = this.getTableColumnCompositions(filteredHeadersY, table, contentSvc);
				if (!columnConfigurations || !columnConfigurations.primaryHeaders || !columnConfigurations.body) {
					console.error(`Column configurations are not defined, table: ${this.name} will not render!`);
					return;
				}

				const isSecondaryHeaderToAdd = !isEmpty(columnConfigurations.secondaryHeaders);

				const primaryHeaderBoxModelConstraint = { ...ScenarioHelper.getHeaderBorder(true, isSecondaryHeaderToAdd), marginTop: 5 };
				const primaryHeaderRowPanel = TableCompositionHelper.getHeaderRowPanel('ST Primary Header', columnConfigurations.primaryHeaders, primaryHeaderBoxModelConstraint);
				rows.push(primaryHeaderRowPanel);

				const secondaryHeaderBoxModelConstraint = { ...ScenarioHelper.getHeaderBorder(false), marginTop: 0 };
				const secondaryHeaderRowPanel = TableCompositionHelper.getHeaderRowPanel(
					'ST Secondary Header',
					columnConfigurations.secondaryHeaders,
					secondaryHeaderBoxModelConstraint
				);
				rows.push(secondaryHeaderRowPanel);

				const pageBreakHeaderColumnPanel = new ColumnPanel('', [primaryHeaderRowPanel, secondaryHeaderRowPanel]);

				columnConfigurations.body.forEach((configs: Array<ColumnComposition>, index: number) => {
					const border = Object.assign({}, ScenarioHelper.rowBorder);
					if (index === columnConfigurations.body.length - 1) {
						border.borderBottomColor = ScenarioHelper.hardBorderColour;
					}
					const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
					rows.push(new RowPanel('ST Body Row', rowRenderer, border, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
				});

				panels.push(new BreakableRenderer(rows, {}, pageBreakHeaderColumnPanel, 4));
			});
			panels.push(new DisclosureComponent(disclaimerAfterTableDcr, ScenarioTesterResultsDcr.ST_DISCLAIMER_AFTER_RESULTS_TABLE, {}, { marginTop: 15, marginBottom: 10 }));
		}

		return panels;
	}

	private addAsOfDateToPanels(componentData: ComponentData): RowPanel {
		// add as of date
		const portfolioData = get(componentData, 'portfolioData[0]', []);
		const asOfDateCellData = portfolioData.find(column => column.config.id === 'asOfDate');

		return new RowPanel(
			'As of date + Scenario Fee Amount',
			[
				new CellPanel(
					get(asOfDateCellData, 'formattedValue')
						? [new StaticParagraphComponent('ST_RESULTS_AS_OF_DATE', 'As of {0}.', { 0: asOfDateCellData.formattedValue }, { marginTop: 5 })]
						: [],
					{ proportion: 8 },
					Alignment.Horizontal.Left
				),
				new CellPanel(
					this.enableScenarioAdvisoryFee
						? [
							this.advisoryFee
								? new StaticParagraphComponent(
									'CUSTOM_ADVISORY_FEE_AMOUNT_ENABLED_TEXT_DYNAMIC_PDF',
									'Scenario potential impact is net of {0}% advisory fee.',
									{ 0: this.advisoryFee },
									{ marginTop: 5 }
								)
								: new StaticParagraphComponent(
									'CUSTOM_ADVISORY_FEE_AMOUNT_DISABLED_TEXT_DYNAMIC',
									'Scenario potential impact is gross of advisory fee.',
									{},
									{ marginTop: 5 }
								)
						]
						: [],
					{ proportion: 92 },
					Alignment.Horizontal.Left
				)
			],
			{ marginBottom: 5 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Top
		);
	}
	/**
	 * Contains logic to divide the scenarios into different tables. Examples of the result are in the test
	 */
	private populateTableColCountArray(totalNoOfColumns: number, yieldColCount: number, tableColCountArray: number[], noOfScenariosPerMdsGroup: number[] = []): number[] {
		if (totalNoOfColumns + yieldColCount > this.MAX_COL_COUNT) {
			if (totalNoOfColumns / 2 + yieldColCount > this.MAX_COL_COUNT) {
				const noOfColumnsToPush = this.MAX_COL_COUNT - yieldColCount;
				const columnsToFit = this.getNoOfColumnsToFit(noOfScenariosPerMdsGroup, noOfColumnsToPush);
				tableColCountArray.push(columnsToFit);
				totalNoOfColumns = totalNoOfColumns - columnsToFit;
				this.populateTableColCountArray(totalNoOfColumns, yieldColCount, tableColCountArray, noOfScenariosPerMdsGroup);
			} else {
				const noOfColumnsToPush = Math.round(totalNoOfColumns / 2);
				const columnsToFit = this.getNoOfColumnsToFit(noOfScenariosPerMdsGroup, noOfColumnsToPush);
				totalNoOfColumns = totalNoOfColumns - columnsToFit;
				tableColCountArray.push(columnsToFit);
				if (totalNoOfColumns + yieldColCount > this.MAX_COL_COUNT) {
					this.populateTableColCountArray(totalNoOfColumns, yieldColCount, tableColCountArray, noOfScenariosPerMdsGroup);
				} else {
					tableColCountArray.push(totalNoOfColumns);
				}
			}
		} else {
			tableColCountArray.push(totalNoOfColumns);
		}
		return tableColCountArray;
	}

	/**
	 * Checks if all the scenarios that belong to mds groups can all fit in the columns we want to push.
	 * If they all fit, the noOfColumnsToPush is returned.
	 * If they do not fit, it calculates how many scenarios per mds group can fit so that the group is not split and returns it.
	 * If an mds group is added to the columns to push to the table, it is removed from the noOfScenariosPerMdsGroup array.
	 *
	 * @param {Array<number>} noOfScenariosPerMdsGroup array of how many scenarios belong to each mds group (eg [3, 4])
	 * @param {number} noOfColumnsToPush the columns we want to push to the table
	 * @returns number of columns to push to the table without splitting mds groups
	 */
	private getNoOfColumnsToFit(noOfScenariosPerMdsGroup: number[], noOfColumnsToPush: number): number {
		const totalNoOfMdsScenarios = noOfScenariosPerMdsGroup.reduce((a, b) => a + b, 0);

		let mdsColCount = 0;
		if (totalNoOfMdsScenarios > noOfColumnsToPush) {
			while (!isEmpty(noOfScenariosPerMdsGroup) && mdsColCount <= noOfColumnsToPush) {
				const valueToCheck = mdsColCount + noOfScenariosPerMdsGroup[0];
				if (valueToCheck <= noOfColumnsToPush) {
					mdsColCount += noOfScenariosPerMdsGroup.shift();
				} else {
					break;
				}
			}
		} else {
			mdsColCount = noOfColumnsToPush;
		}

		return mdsColCount;
	}

	private getTableColumnCompositions(headersY: CellData[], scenarioData: ScenarioData, contentSvc: ContentService): MultipleHeaderColumnCompositions {
		const firstColumnTitle = contentSvc.get('RESULTS_PORTFOLIOS_ASSET_CLASSES_HEADER', 'Portfolios & Asset Classes');

		const tableHeaders: Array<CellData> = [new CellData(null, firstColumnTitle, firstColumnTitle), ...scenarioData.headersX];
		const mdsGroupHeaders: Array<ScenarioHeaderConfig> = MdsScenarioUtil.getMdsGroupHeader(tableHeaders, this.validMdsScenarios, false, true);
		const portsAssetClasses: Array<CellData> = headersY.filter((header: CellData) => header.config && header.config.visible);
		const columnProportions: Array<number> = this.getColumnProportions(scenarioData.headersX.length + 1);

		const primaryTableHeaders: any[] = isEmpty(mdsGroupHeaders) ? tableHeaders : mdsGroupHeaders;
		const secondaryTableHeaders: Array<CellData> = isEmpty(mdsGroupHeaders) ? [] : tableHeaders;

		return new MultipleHeaderColumnCompositions(
			TableCompositionHelper.getTableHeaderColumnComposition(primaryTableHeaders, columnProportions, true),
			TableCompositionHelper.getTableHeaderColumnComposition(secondaryTableHeaders, columnProportions, false),
			scenarioData.cells
				.filter((row: Array<CellData>) => row[0] && row[0].config && row[0].config.visible)
				.map((row: Array<CellData>, index: number) => {
					const portfolioNameCell = portsAssetClasses[index];
					const portfolioName = get(portfolioNameCell, 'formattedValue', '');

					const dollarFormatter = new NumberFormatter(NumberFormatter.NONE, 0, null, null, NumberFormatter.DOLLAR);
					const aumRawValue = dollarFormatter.format(this.defaultAumValue);
					const aumText = contentSvc.get('RESULTS_DOLLAR_AMOUNT_TEXT', 'Dollar Amount:') + ' ' + aumRawValue;

					return [
						new ColumnComposition({
							renderer: new MultipleLineTextRenderer(
								[
									{
										cell: portfolioName,
										textClasses: ['text-8', 'text-bold'],
										constraints: { paddingTop: 9, paddingLeft: 12, paddingRight: 12 }
									},
									{
										cell: aumText,
										textClasses: ['text-7', 'text-grey'],
										constraints: { paddingBottom: 9, paddingLeft: 12, paddingRight: 12 }
									}
								],
								Alignment.Horizontal.Left,
								Alignment.Vertical.Middle,
								columnProportions[0]
							)
						}),
						...row.map((cell: CellData, cellIndex: number) => {
							const isYieldCell = this.includeYield && cellIndex === 0;
							const valueAmountCell = ScenarioHelper.getValueAmount(cell, this.defaultAumValue, !isYieldCell);

							const columnComposition = new ColumnComposition({
								renderer: new MultipleLineTextRenderer(
									[
										{
											cell: this.isDollarAmountDefault ? valueAmountCell : cell,
											textClasses: ['text-8', ...ScenarioHelper.getCellTextClasses(cell, cellIndex, this.includeYield, true)],
											constraints: { paddingTop: ScenarioHelper.getVerticalCellPadding(portfolioName), paddingLeft: 12, paddingRight: 12 }
										},
										{
											cell: this.isDollarAmountDefault ? cell : valueAmountCell,
											textClasses: ['text-7', ...ScenarioHelper.getCellTextClasses(cell, cellIndex, this.includeYield, false)],
											constraints: { paddingBottom: ScenarioHelper.getVerticalCellPadding(portfolioName), paddingLeft: 12, paddingRight: 12 }
										}
									],
									Alignment.Horizontal.Right,
									Alignment.Vertical.Middle,
									columnProportions[cellIndex + 1],
									{ ...ScenarioHelper.cellBorder }
								)
							});

							return columnComposition;
						})
					];
				})
		);
	}

	/*************************************************
	 * Private methods
	 *************************************************/

	/**
	 * 	Divides data into arrays to make them ready to be rendered on separate tables, according to the produced tableColCountArray sequence
	 * 	Adds yield if needed
	 */
	private sharedScenarioTables(tableColCountArray: number[], scenarioData: ScenarioData, yieldTabData: TabData): ScenarioData[] {
		const shardedScenarioTables: ScenarioData[] = [];
		const yieldData: CellData[] = [];
		tableColCountArray.forEach((tableColCount, tableIdx) => {
			let newHeadersX: CellData[] = [];
			let scenarioTable: CellData[][] = [];

			scenarioData.cells.forEach(row => {
				scenarioTable.push(row.splice(0, tableColCount));
			});

			if (this.includeYield) {
				const filteredYieldTabDataCells = yieldTabData.tableData[0].cells.filter((cells, idx) => this.selectedAssetClassesIdx.includes(idx));

				newHeadersX.push(get(yieldTabData, 'tableData[0].headersX[0]'));

				scenarioTable = scenarioTable.map((arr, i) => {
					yieldData.push(...filteredYieldTabDataCells[i]);
					return [...filteredYieldTabDataCells[i], ...arr];
				});
				scenarioTable = scenarioTable.map((cells, rowIdx) => {
					return cells.map((cell, i) => {
						if (i === 0) {
							// return cell as it is (index 0 column is yield column)
							return cell;
						} else {
							// return cell with yield value added
							ScenarioHelper.updatePercentageCellData(cell, yieldData[rowIdx].rawValue as string, true, this.advisoryFee);
							return cell;
						}
					});
				});
			} else {
				// when yield is off 1/365th of advisory fee will be deducted
				scenarioTable = scenarioTable.map(cells => {
					return cells.map(cell => {
						ScenarioHelper.updatePercentageCellData(cell, '0', false, this.advisoryFee);
						return cell;
					});
				});
			}

			newHeadersX = [...newHeadersX, ...scenarioData.headersX.splice(0, tableColCount)];
			shardedScenarioTables.push({ headersX: newHeadersX, cells: scenarioTable });
		});
		return shardedScenarioTables;
	}

	private getColumnProportions(numOfColumns: number): Array<number> {
		switch (numOfColumns) {
			case 1:
				return [100];
			case 2:
				return [28, 72];
			case 3:
				return [28, 36, 36];
			case 4:
				return [22, 26, 26, 26];
			case 5:
				return [22, 19.5, 19.5, 19.5, 19.5];
			case 6:
				return [22, 15.6, 15.6, 15.6, 15.6, 15.6];
			case 7:
				return [22, 13, 13, 13, 13, 13, 13];
			case 8:
				return [22, 11.14, 11.14, 11.14, 11.14, 11.14, 11.15, 11.15];
			default:
				console.error('Number of columns was not between the expected range 1 to 8 - received was: ' + numOfColumns);
		}
	}
}
